<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ProductCard from '@/Components/Public/ProductCard.vue'

// Horizontal swipe rail of product cards for the public home. One component
// for all three product types (vehicle | tour | property) — swipe/drag on
// touch, prev/next arrows on desktop. Cards are the shared ProductCard.
const props = defineProps({
  title:        { type: String, required: true },
  kind:         { type: String, required: true }, // vehicle | tour | property | transfer | package
  items:        { type: Array,  default: () => [] },
  viewAllHref:  { type: String, default: '' },
  itemBase:     { type: String, default: '' },     // card link = `${itemBase}/${id}`
  emptyText:    { type: String, default: '' },
  emptyCtaText: { type: String, default: '' },
  emptyCtaHref: { type: String, default: '' },
})

const { t } = useI18n()
const track = ref(null)

const accentVar = computed(() => (
  // Pillar colorways per the design system: vehicle coral, tour lagoon, property ink.
  { vehicle: 'var(--accent)', tour: 'var(--second)', property: 'var(--ink)', transfer: 'var(--warn)', package: 'var(--second)' }[props.kind] || 'var(--accent)'
))

function nudge(dir) {
  const el = track.value
  if (el) el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: 'smooth' })
}
</script>

<template>
  <section v-if="items.length || emptyText" class="rail" :style="{ '--rail-accent': accentVar, '--card-accent': accentVar }">
    <div class="rail__head">
      <h2 class="rail__title">
        <span class="rail__icon">
          <svg v-if="kind === 'vehicle'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="5.5" cy="17.5" r="3.5" /><circle cx="18.5" cy="17.5" r="3.5" />
            <path d="M15 5.5a1 1 0 1 0 0-.01" /><path d="M12 17.5V14l-3-3 4-3 2.5 3.5H18" />
          </svg>
          <svg v-else-if="kind === 'tour'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" /><polygon points="16.2 7.8 13.4 13.4 7.8 16.2 10.6 10.6" />
          </svg>
          <svg v-else-if="kind === 'package'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M4 8.5 12 4l8 4.5-8 4.5-8-4.5z" /><path d="M4 8.5V16l8 4.5 8-4.5V8.5" /><path d="M12 13v7.5" />
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M3 10.5 12 3l9 7.5" /><path d="M5 10v9.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V10" /><path d="M9.5 20.5V15h5v5.5" />
          </svg>
        </span>
        {{ title }}
      </h2>
      <a v-if="items.length" :href="viewAllHref" class="rail__all">{{ t('public.home.view_all') }} →</a>
    </div>

    <div v-if="items.length" class="rail__wrap">
      <button type="button" class="rail__arrow rail__arrow--prev" :aria-label="t('public.home.prev')" @click="nudge(-1)">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
      </button>
      <div ref="track" class="rail__track">
        <div v-for="it in items" :key="it.id" class="rail__item">
          <ProductCard :kind="kind" :item="it" :href="`${itemBase}/${it.id}`" />
        </div>
      </div>
      <button type="button" class="rail__arrow rail__arrow--next" :aria-label="t('public.home.next')" @click="nudge(1)">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
      </button>
    </div>

    <div v-else class="rail__empty">
      <p class="rail__empty-text">{{ emptyText }}</p>
      <a v-if="emptyCtaHref" :href="emptyCtaHref" class="rail__empty-cta">{{ emptyCtaText }}</a>
    </div>
  </section>
</template>

<style scoped>
.rail { margin-top: 2.75rem; }
.rail:first-child { margin-top: 0; }
.rail__head {
  display: flex; align-items: center; justify-content: space-between; gap: 1rem;
  margin-bottom: 1.1rem;
}
.rail__title {
  display: inline-flex; align-items: center; gap: 0.6rem;
  font-family: var(--font-display, var(--display));
  font-weight: 800; font-size: clamp(1.4rem, 3vw, 1.9rem);
  letter-spacing: -0.02em; color: var(--ink); margin: 0;
}
.rail__icon {
  display: grid; place-items: center;
  width: 2.4rem; height: 2.4rem; border-radius: 0.7rem;
  background: color-mix(in oklch, var(--rail-accent) 14%, var(--surface));
  color: var(--rail-accent); flex: none;
}
.rail__icon svg { width: 1.4rem; height: 1.4rem; }
.rail__all {
  font-weight: 700; font-size: 1rem; color: var(--rail-accent);
  text-decoration: none; white-space: nowrap;
}
.rail__all:hover { text-decoration: underline; }

.rail__wrap { position: relative; }
.rail__track {
  display: flex; gap: 1rem;
  overflow-x: auto; scroll-snap-type: x mandatory; scroll-behavior: smooth;
  padding: 0.25rem 0.25rem 1rem;
  scrollbar-width: none;
}
.rail__track::-webkit-scrollbar { display: none; }
.rail__item { flex: 0 0 auto; width: min(80vw, 17rem); scroll-snap-align: start; }

.rail__arrow {
  position: absolute; top: 38%; transform: translateY(-50%); z-index: 2;
  width: 2.75rem; height: 2.75rem; border-radius: 999px;
  display: grid; place-items: center;
  background: var(--surface); border: 1px solid var(--line); color: var(--ink);
  cursor: pointer;
  box-shadow: 0 8px 22px -10px color-mix(in oklch, var(--ink) 40%, transparent);
}
.rail__arrow svg { width: 1.3rem; height: 1.3rem; }
.rail__arrow:hover { border-color: var(--rail-accent); color: var(--rail-accent); }
.rail__arrow--prev { left: -0.5rem; }
.rail__arrow--next { right: -0.5rem; }
@media (max-width: 767px) { .rail__arrow { display: none; } }

.rail__empty {
  display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem 1rem;
  padding: 1.75rem; border: 1px dashed var(--line); border-radius: 1rem;
  background: color-mix(in oklch, var(--rail-accent) 5%, var(--surface));
}
.rail__empty-text { margin: 0; font-size: 1.05rem; color: var(--inkSoft); }
.rail__empty-cta { font-weight: 700; font-size: 1.05rem; color: var(--rail-accent); text-decoration: none; }
.rail__empty-cta:hover { text-decoration: underline; }
</style>
